/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";
import config from "../config/userLists.config.json";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_PRESENTATIONS, GET_SECTION_OPTIONS } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { pretty } from "@vlinder-web/utils-react";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ISectionOptionsMethods extends IPluginMethodMap {
  getSectionOptionsProps(args: any): Promise<any>;
  getAllSectionOptions(args: any): Promise<any>;
}

export class SectionOptionsService implements IAgentPlugin {
  readonly methods: ISectionOptionsMethods;

  constructor() {
    this.methods = {
      getSectionOptionsProps: this.getSectionOptionsProps.bind(this),
      getAllSectionOptions: this.getAllSectionOptions.bind(this),
    };
  }

  async getAllSectionOptions(args: any) {
    console.log("args getAllSectionOptions==>", args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const _res = await client.query({
          query: GET_PRESENTATIONS,
          variables: {
            filter: {
              status: {
                eq: "active",
              },
            },
          },
          fetchPolicy: "network-only",
        });
        const presentationId = args?.pid
          ? args?.pid
          : _res?.data?.getPresentations?.content[0]?.id;

        if (!presentationId) {
          return {
            sectionOptionsFilter: [],
          };
        }

        const filter = {
          commonSearch: args?.commonSearch ?? null,
          presentation: {
            eq: presentationId ?? null,
          },
        };
        const res = await client.query({
          query: GET_SECTION_OPTIONS,
          variables: {
            filter: filter,
          },
          fetchPolicy: "network-only",
        });

        if (res?.data) {
          const costumeTypes =
            res?.data?.getCostumeTypePartitions?.content || [];
          return {
            sectionOptionsFilter: this.getSearchFilter(costumeTypes),
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async getSectionOptionsProps(args: any) {
    console.log("args getSectionOptionsProps==>", pretty(args));
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        window.open;
        const _res = await client.query({
          query: GET_PRESENTATIONS,
          variables: {
            filter: {
              status: {
                eq: "active",
              },
            },
          },
          fetchPolicy: "network-only",
        });
        const presentationId = args?.pid
          ? args?.pid
          : _res?.data?.getPresentations?.content[0]?.id;

        if (!presentationId) {
          return {
            ...config,
            ultUsers: [],
            limitTotal: 0,
            remainingTotal: 0,
            registeredTotal: 0,
            isEmpty: true,
            activePage: 1,
            currentPage: 1,
            pageSize: 0,
            totalCount: 0,
            totalPages: 1,
            sectionOptionsFilter: [],
          };
        }

        const filter = {
          commonSearch: args?.commonSearch ?? null,
          presentation: {
            eq: presentationId ?? null,
          },
        };
        const res = await client.query({
          query: GET_SECTION_OPTIONS,
          variables: {
            filter: filter,
            options: {
              limit: args?.size ?? 10,
              skip: args?.offset ?? 0,
            },
          },
          fetchPolicy: "network-only",
        });

        if (res?.data) {
          const costumeTypes =
            res?.data?.getCostumeTypePartitions?.content || [];
          const count = res?.data?.getCostumeTypePartitions?.count || 0;
          const limit = res?.data?.getCostumeTypePartitions?.limit || 10;
          const skip = res?.data?.getCostumeTypePartitions?.skip || 0;

          let _totalPages = count ? Math.ceil(parseInt(count) / 10) : 1;

          let _currentPage = limit ? skip / limit + 1 : 1;
          let _rowData = this.formatTable(costumeTypes);
          let _rowDataTotal = this.getTotal(costumeTypes);
          console.log("_rowData==>", _rowDataTotal[0]?.limitTotal);
          return {
            ...config,
            ultUsers: _rowData,
            limitTotal: _rowDataTotal[0]?.limitTotal ?? 0,
            remainingTotal: _rowDataTotal[0]?.remainingTotal ?? 0,
            registeredTotal: _rowDataTotal[0]?.registeredTotal ?? 0,
            isEmpty: costumeTypes.length <= 0,
            activePage: _currentPage,
            currentPage: _currentPage,
            pageSize: _rowData.length,
            totalCount: count,
            totalPages: _totalPages,
            sectionOptionsFilter: this.getSearchFilter(costumeTypes),
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
  private getSearchFilter(costumeTypes: any[]): any[] {
    let _rowData: any = [];
    costumeTypes.forEach((item) => {
      _rowData.push({
        label: item.name,
        value: item.id,
      });
    });
    return _rowData;
  }
  private calculateInventory(item: any): number {
    return (
      (item?.inventoryDetail?.defaultInvitesRemaining ?? 0) +
      (item?.inventoryDetail?.defaultInvitesUsed ?? 0) +
      (item?.inventoryDetail?.specialInvitesRemaining ?? 0) +
      (item?.inventoryDetail?.specialInvitesUsed ?? 0) +
      (item?.inventoryDetail?.sectionSpecificInvitesRemaining ?? 0) +
      (item?.inventoryDetail?.sectionSpecificInvitesUsed ?? 0)
    );
  }

  private formatTable(costumeTypes: any[]): any[] {
    let _rowData: any = [];
    costumeTypes.forEach((item) => {
      const defaultInvites =
        (item?.inventoryDetail?.defaultInvitesRemaining ?? 0) +
        (item?.inventoryDetail?.defaultInvitesUsed ?? 0);
      const specialInvites =
        (item?.inventoryDetail?.specialInvitesRemaining ?? 0) +
        (item?.inventoryDetail?.specialInvitesUsed ?? 0);
      const sectionSpecificInvites =
        (item?.inventoryDetail?.sectionSpecificInvitesRemaining ?? 0) +
        (item?.inventoryDetail?.sectionSpecificInvitesUsed ?? 0);

      _rowData.push({
        sectionName: item?.section?.name ?? "-",
        sectionOptionName: item?.name ?? "Null",
        id: item?.id,
        code: item?.code,
        name: item?.name,
        presentationName: item?.presentation?.name ?? "-",
        defultInvitesUsed: item?.inventoryDetail?.defaultInvitesUsed,
        sectionSpecificInvitesUsed:
          item?.inventoryDetail?.sectionSpecificInvitesUsed,
        specialInvitesUsed: item?.inventoryDetail?.specialInvitesUsed,
        defultInvitesRemaining: item?.inventoryDetail?.defaultInvitesRemaining,
        sectionSpecificInvitesRemaining:
          item?.inventoryDetail?.sectionSpecificInvitesRemaining,
        specialInvitesRemaining: item?.inventoryDetail?.specialInvitesRemaining,
        isDefaultSold: item?.inventoryDetail?.isDefaultSold,
        isSectionSpecificSold: item?.inventoryDetail?.isSectionSpecificSold,
        isSpecialReserveSold: item?.inventoryDetail?.isSpecialReserveSold,
        historyArray: item?.updateHistory ?? [],
        inventory: this.calculateInventory(item),
        registered:
          (item?.inventoryDetail?.defaultInvitesUsed ?? 0) +
          (item?.inventoryDetail?.sectionSpecificInvitesUsed ?? 0) +
          (item?.inventoryDetail?.specialInvitesUsed ?? 0),
        remaining:
          (item?.inventoryDetail?.defaultInvitesRemaining ?? 0) +
          (item?.inventoryDetail?.sectionSpecificInvitesRemaining ?? 0) +
          (item?.inventoryDetail?.specialInvitesRemaining ?? 0),
        sectionSpecificInvites,
        defaultInvites,
        specialInvites,
      });
    });

    return _rowData;
  }

  private getTotal(costumeTypes: any[]): any[] {
    let _rowData: any = [];
    let result: any = [];
    costumeTypes.forEach((item) => {
      _rowData.push({
        inventory: this.calculateInventory(item),
        registered:
          (item?.inventoryDetail?.defaultInvitesUsed ?? 0) +
          (item?.inventoryDetail?.sectionSpecificInvitesUsed ?? 0) +
          (item?.inventoryDetail?.specialInvitesUsed ?? 0),
        remaining:
          (item?.inventoryDetail?.defaultInvitesRemaining ?? 0) +
          (item?.inventoryDetail?.sectionSpecificInvitesRemaining ?? 0) +
          (item?.inventoryDetail?.specialInvitesRemaining ?? 0),
      });
    });
    let sumInventory = 0;
    let sumRegistered = 0;
    let sumRemaining = 0;

    for (const item of _rowData) {
      sumInventory += item.inventory;
      sumRegistered += item.registered;
      sumRemaining += item.remaining;
    }
    result.push({
      limitTotal: sumInventory,
      registeredTotal: sumRegistered,
      remainingTotal: sumRemaining,
    });
    console.log("_rowData==>", pretty(result));
    return result;
  }
}
